// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { History } from "history";
import { fetchSearchResult } from "data/api";
import {
    type SearchResultDTO,
    type SearchItemDTO,
    type SearchRequestDTO,
    type SearchBucketDTO,
    type SearchValueTypeEnum,
    type SearchFilterDTO,
    type SearchSortDTO,
    type SearchResultRowDTO,
    SortDirectionOptions,
    SearchValueType,
    ListViewMode,
    type ListViewModeEnum,
    type ApiErrorResult,
    type MapProviderConfig,
    type RasterLocation,
    type ArrowNavigationSetter,
    SORT_BUCKET_NAME,
} from "data/types";
import { togglesUpdateCounterAction, getSearchAction } from "data/actions";
import { getRoute } from "components/ItemUriLink";
import CommandActionHandler from "components/CommandActions";
import SettingValue, { type SettingValueProps } from "containers/SettingValue";
import SearchResult from "components/SearchResult";
import SearchResultMap from "components/SearchResultMap";
import SearchResultCalendar from "components/SearchResultCalendar";
import SearchResultGrid from "components/SearchResultGrid";
import FilterBarContainer from "containers/FilterBar";
import type DocModalProps from "components/DocModal";
import Constants, {
    type ConstantsEnum,
    Feature,
    isFeatureOn,
} from "data/constants";
import Status from "components/Status";
import immutable from "object-path-immutable";
import GetDefaultSearchBuckets from "data/FilterBar";
import * as s from "data/reducers/selectors";
import NoData from "components/NoData";
import { SupportGeoMapViewer } from "components/MapViewer/providerSupport";
import { openSearchResultActionPanel } from "components/SearchResultActionPanel";
import { openMultiSelectionActionPanel } from "components/MultiSelectionActionPanel";
import withMultiSelection, {
    type MultiSelectionProps,
} from "containers/MultiSelection";
import styles from "./SearchResult.module.css";

type Props = {
    /** react-router History */
    history: History,
    /** the search requested by the user */
    searchItem?: SearchItemDTO,
    /** optional extra filters to set on searchRequest */
    extraFilters?: Array<SearchFilterDTO>,
    /** fetch status */
    status: ConstantsEnum,
    /** fetch error details */
    error?: ApiErrorResult,
    /** search results */
    result: ?SearchResultDTO,
    /** the words to highlight */
    terms: Array<string>,
    /** last searchRequest sent to server (from Redux store) */
    searchRequest: SearchRequestDTO,
    /** callback when setting activeRowIndex
     * @param {number} activeRowIndex new row index
     * @param {SearchResultRowDTO} row row object that was clicked
     * @returns {bool} return FALSE to avoid opening DocViewer for Doc itemUris
     */
    onSetActiveRowIndex?: (
        activeRowIndex: number,
        row?: SearchResultRowDTO
    ) => boolean,
    /** callback when searchForm.autoBrowseToItem is true and one result found
     * @default undefined will navigate to the folder view
     * @param {number} activeRowIndex new row index (0)
     * @param {SearchResultRowDTO} row row object that was clicked
     * */
    onAutoBrowseToItem?: (
        activeRowIndex: number,
        row?: SearchResultRowDTO
    ) => void,
    /** search result row to mark as active */
    activeRowIndex: number,
    /** whether the breadcrumbs should allow navigation */
    isBreadcrumbReadonly?: boolean,
    /** current view mode */
    viewModeSetting: SettingValueProps,
    /** current result sorting */
    sortSetting: SettingValueProps,
    /** Redux Dispatch */
    dispatch: Dispatch,
    /** optional specific ViewMode */
    viewMode: ?ListViewMode,
    /** optional whether to show filterbar */
    showFilterBar: ?boolean,
    /** custom mapProvider to use for Map viewMode */
    mapProvider?: MapProviderConfig,
    /** callback when user clicks an empty space on the map (only when useMarker=false & mapProvider.onClick exists) */
    onMapClick?: (location: RasterLocation) => void,
    /** whether to hide More Action or not */
    isMoreHidden: ?boolean,
    /** ArrowNavigation Setter Functions */
    postMessage: ?ArrowNavigationSetter,
    /** withMultipleSelection additional props */
    multiSelection: MultiSelectionProps,
};

type State = {
    aggregations: ?Array<SearchBucketDTO>,
    selected: Array<SearchFilterDTO>,
    scrollToRow: number,
    documents: any,
};

export class SearchResultContainer extends PureComponent<Props, State> {
    _isMounted: boolean;

    constructor(props: Props) {
        super(props);
        this._isMounted = false;
        this.state = {
            aggregations: GetDefaultSearchBuckets(),
            selected: [],
            scrollToRow: props.activeRowIndex,
            documents: {},
        };
    }

    componentDidMount = () => {
        this._isMounted = true;
        if (this.props.viewMode) {
            this._maybeChangeViewMode(this.props.viewMode);
        } else {
            this._maybeChangeViewMode();
        }
        this.props.searchItem &&
            this._fetch(this._prepSearchRequest(this.props.searchItem));
    };

    componentWillUnmount = () => (this._isMounted = false);

    componentDidUpdate(prevProps: Props) {
        /* istanbul ignore else */
        if (prevProps.searchItem !== this.props.searchItem) {
            this.props.multiSelection?.disable();
            this.setState(
                {
                    aggregations: GetDefaultSearchBuckets(),
                    selected: [],
                },
                () =>
                    this._fetch(this._prepSearchRequest(this.props.searchItem))
            );
        }

        if (
            prevProps.result !== this.props.result &&
            this.props.status === Constants.OK
        ) {
            const result = this.props.result;
            /* istanbul ignore else */
            if (result != null) {
                if (
                    this.props.result.autoBrowseToItem === true &&
                    result.totalCount === 1
                ) {
                    if (typeof this.props.onAutoBrowseToItem === "function") {
                        this.props.onAutoBrowseToItem(0, result.rows[0]);
                    } else {
                        this.props.history.replace(
                            getRoute(result.rows[0].itemUri, false, false)
                        );
                    }
                    return;
                }
                // first result page? update counter
                this._updateToggleCounter(result.totalCount);
                /* istanbul ignore next */
                const aggregationsFromResult =
                    result.aggregations != null ? result.aggregations : [];
                let aggregations: Array<SearchBucketDTO> =
                    GetDefaultSearchBuckets()
                        .concat(
                            aggregationsFromResult.filter(
                                // Hide Aggregations from result that are provided via DefaultSearchBuckets
                                (a) =>
                                    !GetDefaultSearchBuckets().some(
                                        (b) =>
                                            b.name.toUpperCase() ===
                                            a.name.toUpperCase()
                                    )
                            )
                        )
                        .sort((a, b) => {
                            var nameA = a.name.toUpperCase(); // ignore upper and lowercase
                            var nameB = b.name.toUpperCase(); // ignore upper and lowercase
                            if (nameA < nameB) {
                                return -1;
                            }
                            /* istanbul ignore else */
                            if (nameA > nameB) {
                                return 1;
                            }

                            // names must be equal
                            /* istanbul ignore next */
                            return 0;
                        });

                // update bucket.selected
                this.state.selected.forEach((filter) => {
                    /* istanbul ignore else */
                    if (filter.value != null) {
                        const selectIndex = aggregations.findIndex(
                            (i) => i.name === filter.name
                        );
                        /* istanbul ignore else */
                        if (selectIndex !== -1) {
                            aggregations[selectIndex].selected = [filter.value];
                        }
                    }
                });

                this._handleMoreRowsLoaded(
                    result.rows,
                    result.totalCount,
                    false,
                    {
                        aggregations,
                    }
                );

                this.props.multiSelection?.disable();
            }
        }
        /* istanbul ignore next */
        if (this.props.activeRowIndex !== prevProps.activeRowIndex) {
            this.setState((state) => {
                this._updateArrowNavigationRows(state.documents);
                return {
                    scrollToRow: state.scrollToRow || this.props.activeRowIndex, //#59968
                };
            });
        }
    }

    _updateToggleCounter(count: number, loading?: boolean): void {
        let { dispatch } = this.props;
        let status = Constants.OK;
        if (loading === true) {
            status = Constants.LOADING;
        }
        /** Need to update both toggles for consistent UX */
        dispatch(
            togglesUpdateCounterAction({
                name: "searchresult",
                counts: { result: count },
                status: { result: status },
            })
        );
        dispatch(
            togglesUpdateCounterAction({
                name: "search",
                counts: { result: count },
                status: { result: status },
            })
        );
    }

    _prepSearchRequest = (searchItem: SearchItemDTO): SearchRequestDTO => {
        // let's build a searchRequest from an existing SearchItem
        const searchRequest: SearchRequestDTO = {
            pageNum: 0,
            id: searchItem.id,
            name: searchItem.name,
            searchType: searchItem.searchType,
            isCustom: false,
            searchText: searchItem.searchText,
            itemUri: searchItem.itemUri,
            scope: searchItem.scope,
            filters: (searchItem.filters || []).concat(
                this.props.extraFilters || []
            ),
            sorts: searchItem.sorts || [],
            nodeType: searchItem.nodeType,
            fakeUris: searchItem.fakeUris,
        };
        // any filterBar filters from searchItem? update bucket.selected
        const bucketFilters = searchRequest.filters.filter(
            (f) => f.valueType !== SearchValueType.NotSet && f.value != null
        );
        /** istanbul ignore else */
        if (bucketFilters.length > 0) {
            this.setState((prevState: state) => ({
                selected: prevState.selected.concat(bucketFilters),
            }));
        }
        return searchRequest;
    };

    // load first page of results
    _fetch(searchRequest: SearchRequestDTO, hideLoading?: boolean) {
        this._updateToggleCounter(0, true);
        this.props.dispatch(getSearchAction({ searchRequest }));
    }

    // load next page of results
    loadMoreRows = (pageNum: number): Promise<SearchResultDTO> => {
        const { searchRequest, result } = this.props;
        /** Trigger loading only on MapViewMode, due to totalCount on ListView */
        const hasMapViewMode =
            this.props.viewModeSetting.value === ListViewMode.Map;
        this._updateToggleCounter(
            /*istanbul ignore next */ result && !hasMapViewMode
                ? result.totalCount
                : 0,
            hasMapViewMode
        );

        // TODO use Redux action instead of directly loading from fetch!
        return fetchSearchResult(
            immutable.set(searchRequest, "pageNum", pageNum)
        ).then((result) => {
            /* istanbul ignore next */
            if (!this._isMounted) {
                return;
            }
            this._handleMoreRowsLoaded(result.rows, result.totalCount, true);
            return result;
        });
    };

    _onFilterBarSelect = (
        name: string,
        valueType: SearchValueTypeEnum,
        value: any
    ) => {
        const { selected } = this.state;
        const { searchRequest } = this.props;
        let newSelected = selected;
        let newSearchRequest = searchRequest;

        // changing a previous loaded one?
        /* istanbul ignore else */
        if (searchRequest.isCustom === false) {
            newSearchRequest = immutable.set(searchRequest, "isCustom", true);
        }

        // do we already a filter set?
        const selectIndex = selected.findIndex((i) => i.name === name);
        const filterIndex = searchRequest.filters.findIndex(
            (i) => i.name === name
        );
        if (filterIndex !== -1) {
            newSearchRequest = immutable.del(
                searchRequest,
                "filters." + filterIndex
            );
        }

        /* istanbul ignore else */
        if (selectIndex !== -1) {
            newSelected = immutable.del(selected, String(selectIndex));
        }

        /* istanbul ignore else */
        if (value === "__ALL__" && filterIndex !== -1) {
            // enum value? set to 0 (NotSet/Unknown) instead of deleting it (otherwise it cannot overwrite any seachForm additionalParameters)
            if (
                [
                    SearchValueType.ItemType,
                    SearchValueType.FileSize,
                    SearchValueType.RelativeDate,
                ].includes(searchRequest.filters[filterIndex].valueType)
            ) {
                newSearchRequest = immutable.set(
                    searchRequest,
                    ["filters", filterIndex, "value"],
                    0
                );
            }
        }

        /* istanbul ignore else */
        if (value !== "__ALL__") {
            // add new filter
            newSelected = newSelected.concat([{ name, value, valueType }]);
            if (name !== SORT_BUCKET_NAME) {
                const newFilter: SearchFilterDTO = {
                    name,
                    valueType,
                    value,
                };
                newSearchRequest = immutable.push(
                    newSearchRequest,
                    "filters",
                    newFilter
                );
            } else {
                const newSort: SearchSortDTO = {
                    name: value,
                    valueType,
                    direction:
                        searchRequest.sorts &&
                        searchRequest.sorts[0] &&
                        searchRequest.sorts[0].direction ===
                            SortDirectionOptions.Ascending
                            ? SortDirectionOptions.Descending
                            : SortDirectionOptions.Ascending,
                };
                // TODO support multiple sort columns?
                newSearchRequest = immutable.set(newSearchRequest, "sorts", [
                    newSort,
                ]);
                this.props.sortSetting.setValue(value);
            }
        }

        newSearchRequest = immutable.set(newSearchRequest, "pageNum", 0);
        this.setState({ selected: newSelected }, () => {
            /* istanbul ignore else */
            if (typeof this.props.onSetActiveRowIndex === "function")
                this.props.onSetActiveRowIndex(-1);
            this._fetch(newSearchRequest, true);
        });
    };

    _onFilterBarClear = () => {
        const { searchRequest, searchItem } = this.props;
        this.setState(
            {
                selected: [],
            },
            () => {
                if (searchItem) {
                    this._fetch(this._prepSearchRequest(searchItem), true);
                    return;
                }

                let newSearchRequest = searchRequest;
                newSearchRequest = immutable.set(
                    newSearchRequest,
                    "filters",
                    []
                );
                newSearchRequest = immutable.set(newSearchRequest, "sorts", []);
                // TODO FilterBarClear should remove ALL BUT HIDDEN FILTERS!
                this._fetch(newSearchRequest, true);
            }
        );
    };

    //#region docmodal
    _isDocument = (row: SearchResultRowDTO): boolean => row.isDoc === true;

    _getNextDocumentIndex = (index) => {
        for (let i = index; i < this.props.result.totalCount; ++i) {
            const row = this._getDocument(i);
            /* istanbul ignore else */
            if (typeof row !== "undefined") {
                return i;
            }
        }
        return index - 1;
    };

    _getPrevDocumentIndex = (index: number): number => {
        for (let i = index; i >= 0; --i) {
            const row = this._getDocument(i);
            /* istanbul ignore else */
            if (typeof row !== "undefined") {
                return i;
            }
        }
        return index + 1;
    };

    _getDocument = (index: number): ?SearchResultRowDTO => {
        const row = Object.prototype.hasOwnProperty.call(
            this.state.documents,
            index
        );
        // Return only documents from multi selection
        if (
            this.props.multiSelection?.multiple &&
            !this.props.multiSelection?.activeRows.hasOwnProperty(index)
        ) {
            return undefined;
        }
        if (!row) {
            return undefined;
        } else {
            this.setState({
                scrollToRow: index,
            });
            return this.state.documents[index];
        }
    };

    _updateArrowNavigationRows = (documents = this.state.documents) => {
        const { postMessage, multiSelection, result } = this.props;
        // Update postMessage rows if applicable
        /* istanbul ignore next */
        if (result?.totalCount && postMessage?.setRows) {
            if (multiSelection?.multiple) {
                const reducedDocuments = {};
                Object.keys(documents).forEach((i) => {
                    const index = parseInt(i, 10);
                    if (multiSelection?.activeRows.hasOwnProperty(index)) {
                        reducedDocuments[index] = documents[i];
                    }
                });
                postMessage.setRows({
                    ...reducedDocuments,
                    length: result.totalCount,
                });
            } else {
                postMessage.setRows({
                    ...documents,
                    length: result.totalCount,
                });
            }
        }
    };

    _getDocModalProps = (activeRowIndex: number): DocModalProps => {
        this._updateArrowNavigationRows();
        return {
            activeRowIndex,
            totalCount: this.props.result.totalCount,
            onSetActiveRowIndex: (index: number, isNext: boolean): index => {
                const documentIndex = isNext
                    ? this._getNextDocumentIndex(index)
                    : this._getPrevDocumentIndex(index);
                /* istanbul ignore else */
                if (typeof this.props.onSetActiveRowIndex === "function") {
                    this.props.onSetActiveRowIndex(
                        documentIndex,
                        this._getDocument(documentIndex)
                    );
                }
                return documentIndex;
            },
            getActiveRowItemUri: this._getDocument,
            setTarget: this.props.postMessage?.setTarget,
        };
    };

    setDocuments = (documents: Array<string>) =>
        this.setState({ documents, scrollToRow: -1 });

    _onClick = (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => {
        const prevRowIndex = this.props.activeRowIndex;
        let showViewer = true;
        /* istanbul ignore else */
        if (typeof this.props.onSetActiveRowIndex === "function")
            showViewer = this.props.onSetActiveRowIndex(
                rowIndex,
                e.ctrlKey || e.shiftKey || this.props.multiSelection?.multiple
                    ? null
                    : row
            );
        if (e.ctrlKey) {
            /* istanbul ignore else */
            if (!this.props.multiSelection?.multiple && prevRowIndex > -1) {
                this.props.multiSelection?.actions.handleToggle(prevRowIndex);
            }
            this.props.multiSelection?.actions.handleToggle(rowIndex);
        } else if (e.shiftKey) {
            const startIndex =
                prevRowIndex > rowIndex ? rowIndex : prevRowIndex;
            const endIndex = prevRowIndex > rowIndex ? prevRowIndex : rowIndex;
            for (let i = startIndex; i <= endIndex; i++) {
                /* istanbul ignore if */
                if (i < 0) continue;
                this.props.multiSelection?.actions.handleAdd(i);
            }
        } else if (this.props.multiSelection?.multiple) {
            this.props.multiSelection?.disable();
            e.stopPropagation();
        } else if (this._isDocument(row)) {
            /* istanbul ignore else */
            if (showViewer !== false) {
                CommandActionHandler("doc_preview", {
                    itemUri: row.itemUri,
                    newWindow: e.altKey,
                    searchTerm: (row.foundTerms || this.props.terms)
                        .join(" ")
                        .replace("*", ""),
                    ...this._getDocModalProps(rowIndex),
                });
            }
        }
    };

    _onMoreClick = (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => {
        /* istanbul ignore else */
        if (typeof this.props.onSetActiveRowIndex === "function")
            this.props.onSetActiveRowIndex(rowIndex);
        if (
            this.props.multiSelection?.multiple &&
            Object.keys(this.props.multiSelection?.activeRows).length > 1 &&
            Object.prototype.hasOwnProperty.call(
                this.props.multiSelection?.activeRows,
                rowIndex
            )
        ) {
            openMultiSelectionActionPanel({
                ...this._getDocModalProps(rowIndex),
                activeRows: this.props.multiSelection?.activeRows,
                targetView: "searchresult",
                mapper: /* istanbul ignore next */ (row) => ({
                    name: row.title,
                    itemUri: row.itemUri,
                    formatId: row.breadcrumb?.formatId,
                    isDoc: row.isDoc,
                }),
            });
        } else {
            openSearchResultActionPanel({
                row: { ...row, targetView: "searchresult" },
                ...this._getDocModalProps(rowIndex),
            });
        }
    };
    //#endregion

    _maybeChangeViewMode = (newViewMode: ?ListViewModeEnum): void => {
        let viewMode, fallbackViewMode;

        /** Decide if the current or future ViewMode might be changed */
        if (newViewMode) {
            viewMode = newViewMode;
        } else {
            viewMode = this.props.viewModeSetting.value;
        }
        /** Set fallback ViewMode if current/future ViewMode is not supported */
        if (!SupportGeoMapViewer() && viewMode === ListViewMode.Map) {
            console.warn(
                `GeoMapViewer support is disabled. Using fallback viewMode "${ListViewMode.List}"`
            );
            fallbackViewMode = ListViewMode.List;
        }
        /** Change ViewMode if there is a fallbackViewMode or a newViewMode*/
        if (fallbackViewMode) {
            this.props.viewModeSetting.setValue(fallbackViewMode);
        } else if (
            this.props.viewModeSetting &&
            newViewMode !== this.props.viewModeSetting.value
        ) {
            this.props.viewModeSetting.setValue(newViewMode);
        }

        /** Update toggle count on List */
        if (
            newViewMode &&
            newViewMode === ListViewMode.List &&
            this.props.result
        ) {
            this._updateToggleCounter(this.props.result.totalCount);
        }
    };

    _onViewModeChange = (viewMode: ListViewModeEnum): void =>
        this._maybeChangeViewMode(viewMode);

    _getAvailableViewModes = () => {
        const viewModes = [ListViewMode.List, ListViewMode.Grid];
        if (SupportGeoMapViewer()) viewModes.push(ListViewMode.Map);
        if (isFeatureOn(Feature.searchCalendar))
            viewModes.push(ListViewMode.Calendar);
        return viewModes;
    };

    _onCountOfGeoResults = (count: number): void =>
        this._updateToggleCounter(count, false);

    // _onTextFilter = (value: ?string) => {
    //     // TODO update searchRequest.freetext and run query again
    //     const { searchRequest } = this.props;
    //     this._fetch(immutable.set(searchRequest, "searchText", value), true);
    // };

    _handleMoreRowsLoaded = (
        rows: Array<any>,
        totalCount: number = 0,
        updateTotalCount = false,
        additionalState = {}
    ) => {
        // Store documents for pagination / multi selection
        const documents = {};
        const availableRows = {};
        for (let index in rows) {
            availableRows[index] = { ...rows[index] };
            /* istanbul ignore if */
            if (!this._isDocument(rows[index])) {
                continue;
            }
            documents[index] = rows[index].itemUri;
        }

        // Update multiSelection available Rows
        /* istanbul ignore next */
        this.props.multiSelection?.setAvailableRows(availableRows);

        this._updateArrowNavigationRows(documents);

        this.setState({ documents, scrollToRow: -1, ...additionalState });
        if (updateTotalCount) this._updateToggleCounter(totalCount);
    };

    _isRowActive = (rowIndex: number, activeState: boolean): boolean => {
        return this.props.multiSelection?.multiple
            ? this.props.multiSelection?.isSelected(rowIndex)
            : activeState;
    };

    render() {
        const {
            status,
            error,
            result,
            terms,
            activeRowIndex,
            isBreadcrumbReadonly,
        } = this.props;
        const { scrollToRow } = this.state;
        if (
            this.props.searchItem == null &&
            result == null &&
            status === Constants.NONE
        ) {
            return <NoData locContext="search" />;
        }

        // custom error message handling
        if (
            status === Constants.ERROR &&
            error != null &&
            error.eventId != null &&
            error.eventId.id === 1500 /*SEARCH_PARSE*/
        ) {
            return (
                <NoData locContext="search">
                    <div>
                        <br />
                        Error parsing, field: <b>{error.extra.field}</b>, value:{" "}
                        <b>{error.extra.value}</b>
                        <br />
                        <br />
                        {error.message}
                    </div>
                </NoData>
            );
        }
        /* istanbul ignore else */
        if (status === Constants.OK && result != null) {
            return (
                <div className={styles.wrapper}>
                    {this.props.showFilterBar !== false && (
                        <FilterBarContainer
                            aggregations={this.state.aggregations}
                            selected={this.state.selected}
                            onSelect={this._onFilterBarSelect}
                            onClear={this._onFilterBarClear}
                            viewMode={this.props.viewModeSetting.value}
                            viewModes={this._getAvailableViewModes()}
                            onViewModeChange={this._onViewModeChange}
                        />
                    )}

                    {this.props.viewModeSetting.value === ListViewMode.Map && (
                        <SearchResultMap
                            mapProvider={this.props.mapProvider}
                            onCountOfGeoResults={this._onCountOfGeoResults}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this.props.onMapClick}
                        />
                    )}

                    {this.props.viewModeSetting.value ===
                        ListViewMode.Calendar && (
                        <SearchResultCalendar
                            loadMoreRows={this.loadMoreRows}
                            onMoreClick={
                                this.props.isMoreHidden
                                    ? null
                                    : this._onMoreClick
                            }
                        />
                    )}

                    {this.props.viewModeSetting.value === ListViewMode.Grid && (
                        <SearchResultGrid
                            terms={terms}
                            result={result}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this._onClick}
                            onMoreClick={this._onMoreClick}
                            onContextClick={this._onMoreClick}
                            onFilterBarSelect={this._onFilterBarSelect}
                            activeRowIndex={activeRowIndex}
                            scrollToRow={scrollToRow}
                            searchRequest={this.props.searchRequest}
                            onSetActiveRowIndex={this.props.onSetActiveRowIndex}
                            isMoreHidden={this.props.isMoreHidden}
                            handleMoreRowsLoaded={this._handleMoreRowsLoaded}
                            isRowActive={this._isRowActive}
                            refresh={this.props.multiSelection?.activeRows}
                        />
                    )}
                    {this.props.viewModeSetting.value === ListViewMode.List && (
                        <SearchResult
                            terms={terms}
                            result={result}
                            loadMoreRows={this.loadMoreRows}
                            onClick={this._onClick}
                            onMoreClick={
                                this.props.isMoreHidden
                                    ? null
                                    : this._onMoreClick
                            }
                            activeRowIndex={activeRowIndex}
                            scrollToRow={scrollToRow}
                            isBreadcrumbReadonly={isBreadcrumbReadonly}
                            handleMoreRowsLoaded={this._handleMoreRowsLoaded}
                        />
                    )}
                </div>
            );
        }

        return <Status status={status} />;
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    status: s.searchStatusSelector(state),
    error: s.searchErrorSelector(state),
    searchRequest: s.searchRequestSelector(state),
    terms:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchTermsSelector(state)
            : [""],
    result:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchResultSelector(state)
            : undefined,
    ...ownProps,
});

export default compose(
    withRouter,
    connect(mapStateToProps),
    SettingValue({
        name: "searchResultViewMode",
        namespace: "viewModeSetting",
        defaultValue: ListViewMode.List,
    }),
    SettingValue({
        name: "searchResultSort",
        namespace: "sortSetting",
        defaultValue: "",
    }),
    withMultiSelection({
        delay: 600,
    })
)(SearchResultContainer);
